import createInfoBarMessageViewState from './createInfoBarMessageViewState';
import { rpEncryptMessage } from './createIRMInfoBarMessage.locstring.json';
import loc from 'owa-localize';
import { getLabelFromStore } from 'owa-mail-protection/lib/utils/clp/getExistingCLPInfo';
import ItemPartViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemPartViewState';
import ItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemViewState';
import { lazyShowRevocationModal } from 'owa-mail-revocation';
import { OmeMessageResponseStatus } from 'owa-mail-revocation/lib/store/schema/OmeMessageState';
import hasRevokePermission from 'owa-mail-revocation/lib/utils/hasRevokePermission';
import shouldAppendRevocationInfobar from 'owa-mail-revocation/lib/utils/shouldAppendRevocationInfobar';
import { ClientItem } from 'owa-mail-store';
import Message from 'owa-service/lib/contract/Message';
import {
    InfoBarMessageRank,
    InfoBarMessageViewState,
    InfoBarCustomAction,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import {
    revokeAccess,
    revokeSuccessful,
    revokeFailed,
    revokePartialFailed,
    infoRevoked,
    infoDNF,
} from './createRevocationInfoBarMessage.locstring.json';
import {
    ENCRYPT_DNF_GUID,
    ENCRYPT_GUID,
} from 'owa-mail-protection/lib/store/schema/rms/EncryptionGUID';

/**
 * This infobar is displayed in two cases:
 * 1. For encrypted messages which have CLP label, but do not have a CLP label infobar.
 *    CLP infobars are rendered only in conversation view when any message in the conversation
 *    has a CLP label different from the oldest message. In all other scenarios, CLP label in
 *    subject header suffices. We therefore need a separate infobar for revoke button in those cases.
 * 2. When user revokes the message, OmeMessageResponseStatus changes based on service response.
 *    We render a temporary infobar to show the current revocation status.
 */
export default function tryCreateRevocationInfoBarMessage(
    message: ClientItem,
    key: string,
    itemViewState: ItemPartViewState
): InfoBarMessageViewState {
    if (!message || !itemViewState) {
        return null;
    }

    // Case 1: Message is revocable, response status is none, has encrypting CLP label, and no CLP infobar
    if (
        shouldAppendRevocationInfobar(itemViewState.omeMessageState) &&
        getLabelFromStore(message.MSIPLabelGuid)?.isEncryptingLabel &&
        !itemViewState.shouldShowCLPLabel
    ) {
        return createRevocationInfobar(message, key, itemViewState);
    }

    // Case 2: Message is revocable and either has RMS template, or CLP label
    if (
        hasRevokePermission(itemViewState.omeMessageState) &&
        (message.RightsManagementLicenseData || getLabelFromStore(message.MSIPLabelGuid))
    ) {
        if (itemViewState.omeMessageState.responseState === OmeMessageResponseStatus.None) {
            return null;
        }

        // display text based on revocation service response
        const displayText: string = getInfobarTextForOmeResponse(
            itemViewState.omeMessageState.responseState
        );

        return createInfoBarMessageViewState(key, [displayText], InfoBarMessageRank.Encryption);
    }
    return null;
}

function getInfobarTextForOmeResponse(responseState: OmeMessageResponseStatus): string {
    switch (responseState) {
        case OmeMessageResponseStatus.SuccessOrNoChange:
            return loc(revokeSuccessful);
        case OmeMessageResponseStatus.FailedOrMessageNotFound:
            return loc(revokeFailed);
        case OmeMessageResponseStatus.PartialFailed:
            return loc(revokePartialFailed);
    }
    return null;
}

function createRevocationInfobar(
    message: ClientItem,
    key: string,
    itemViewState: ItemPartViewState
) {
    const messageParts: (InfoBarCustomAction | string)[] = [];

    // Encryption info
    const IRMData = message.RightsManagementLicenseData;
    if (IRMData?.RmsTemplateId === ENCRYPT_GUID) {
        messageParts.push(loc(rpEncryptMessage));
    } else if (IRMData?.RmsTemplateId === ENCRYPT_DNF_GUID) {
        messageParts.push(loc(infoDNF));
    }

    // Revoke button or revocation status
    if (messageParts.length > 0) {
        messageParts.push(' ');
    }
    messageParts.push(getRevocationInfo(itemViewState, message));
    return createInfoBarMessageViewState(key, messageParts, InfoBarMessageRank.Encryption);
}

export function getRevocationInfo(
    itemViewState: ItemViewState,
    item: ClientItem
): InfoBarCustomAction | string {
    const message = item as Message;
    return itemViewState.omeMessageState?.revoked
        ? loc(infoRevoked)
        : {
              text: loc(revokeAccess),
              action: async () => {
                  const showRevocationModal = await lazyShowRevocationModal.import();
                  showRevocationModal(itemViewState.omeMessageState, message.InternetMessageId);
              },
          };
}
